import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Settings, Trash2, Bell, BellOff, Search, MapPin, Briefcase, Building, Clock, Calendar, Euro, X } from 'lucide-react';
import ConfirmModal from '../components/Common/ConfirmModal';
import SelectAllToggle from '../components/Common/SelectAllToggle';
import useConfirmDialog from '../hooks/useConfirmDialog';
import FilterModal from '../components/Jobs/FilterModal';
import WorkFieldCascadePicker, { WorkFieldSelection } from '../components/Common/WorkFieldCascadePicker';
import { LITHUANIAN_CITIES } from '../constants/cities';
import {
  getLocalSavedSearches,
  addLocalSavedSearch,
  updateLocalSavedSearch,
  deleteLocalSavedSearch,
  LocalSavedSearch,
} from '../services/localSavedSearches';
import './SavedSearches.scss';
import './Prenumeratos.scss';

interface SavedSearch {
  id: number | string;
  title: string;
  
  // Employee search fields
  keywords?: string;
  work_field?: string[];
  field_ids?: number[];
  tipas_ids?: number[];
  position_ids?: number[];
  job_type?: string[];
  work_time?: string[];
  salary_min?: number;
  company_activity?: string;
  
  // Employer search fields
  positions?: string[] | string;
  education_level?: string[] | string;
  languages?: string[] | string;
  technical_skills?: string[] | string;
  experience_years?: string;
  desired_work_type?: string[] | string;
  desired_salary?: string;
  
  // Common fields
  location?: string[];
  is_subscribed: boolean;
  notification_frequency?: 'immediate' | 'daily' | 'weekly';
  total_results: number;
  new_results: number;
  created_at: string;
  updated_at: string;
}


const SavedSearches: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmployerMode = searchParams.get('mode') === 'employer';
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSearch, setEditingSearch] = useState<SavedSearch | null>(null);
  const [searchCounts, setSearchCounts] = useState<Record<string | number, { total: number; newCount: number }>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState<number | string | null>(null);
  const [selectedSearches, setSelectedSearches] = useState<(number | string)[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');

  // CV templates
  interface CVTemplate { id: number; category: string; value: string; label?: string; parent_value?: string | null; }
  const [cvTemplates, setCvTemplates] = useState<{ work_field?: CVTemplate[]; work_type?: CVTemplate[]; work_time?: CVTemplate[]; work_location?: CVTemplate[]; company_activity?: CVTemplate[]; position?: CVTemplate[]; position_type?: CVTemplate[]; }>({});
  const [companies, setCompanies] = useState<{ id: number; company_name: string; job_count: number; company_activity?: string }[]>([]);

  // Modal form state — individual fields
  const [formTitle, setFormTitle] = useState('');
  const [formKeywords, setFormKeywords] = useState<string[]>([]);
  const [formLocation, setFormLocation] = useState<string[]>([]);
  const [formWorkFields, setFormWorkFields] = useState<WorkFieldSelection>({ fieldIds: [], tipasIds: [], positionIds: [] });
  const [formWorkFieldLabels, setFormWorkFieldLabels] = useState<string[]>([]);
  const [formJobType, setFormJobType] = useState<string[]>([]);
  const [formWorkTime, setFormWorkTime] = useState<string[]>([]);
  const [formSalaryMin, setFormSalaryMin] = useState('');
  const [formSalarySlider, setFormSalarySlider] = useState(0);
  const [formCompanyActivity, setFormCompanyActivity] = useState('');
  const [formCompanyNames, setFormCompanyNames] = useState<string[]>([]);
  const [formIsSubscribed, setFormIsSubscribed] = useState(true);
  const [formFrequency, setFormFrequency] = useState<'immediate' | 'daily' | 'weekly'>('daily');
  const [activeFilterPanel, setActiveFilterPanel] = useState<string | null>(null);
  const [locationSearch, setLocationSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [keywordInputVal, setKeywordInputVal] = useState('');

  // Taxonomy id → name maps, for rendering saved-search cards (Darbo sritys).
  const [taxFieldNames, setTaxFieldNames] = useState<Record<number, string>>({});
  const [taxTipasNames, setTaxTipasNames] = useState<Record<number, string>>({});

  const mapLocalToSavedSearch = (ls: LocalSavedSearch): SavedSearch => ({
    id: ls.id,
    title: ls.name,
    keywords: ls.keywords.join(', ') || undefined,
    location: ls.location,
    work_field: ls.work_field,
    field_ids: ls.field_ids,
    tipas_ids: ls.tipas_ids,
    position_ids: ls.position_ids,
    job_type: ls.job_type,
    work_time: ls.work_time,
    salary_min: ls.salary_min ?? undefined,
    is_subscribed: false,
    total_results: 0,
    new_results: 0,
    created_at: ls.created_at,
    updated_at: ls.created_at,
  });

  const fetchSavedSearches = useCallback(async () => {
    try {
      setLoading(true);

      if (!isAuthenticated) {
        const local = getLocalSavedSearches().map(mapLocalToSavedSearch);
        setSavedSearches(local);
        return;
      }

      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const endpoint = isEmployerMode 
        ? `${API_URL}/employer-saved-searches/my`
        : `${API_URL}/saved-searches/my`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSavedSearches(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    } finally {
      setLoading(false);
    }
  }, [isEmployerMode, isAuthenticated]);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    fetch(`${API_URL}/cv-templates?grouped=true`).then(r => r.ok ? r.json() : null).then(d => { if (d?.data) setCvTemplates(d.data); }).catch(() => {});
    fetch(`${API_URL}/jobs/companies`).then(r => r.ok ? r.json() : null).then(d => { if (d?.data?.companies) setCompanies(d.data.companies); }).catch(() => {});
    fetch(`${API_URL}/taxonomy`).then(r => r.ok ? r.json() : null).then(d => {
      if (!d?.data) return;
      const fieldMap: Record<number, string> = {};
      (d.data.darboSritys || []).forEach((f: any) => { fieldMap[f.id] = f.name; });
      const tipasMap: Record<number, string> = {};
      (d.data.tipai || []).forEach((t: any) => { tipasMap[t.id] = t.name; });
      setTaxFieldNames(fieldMap);
      setTaxTipasNames(tipasMap);
    }).catch(() => {});
  }, []);

  const toggleArr = (arr: string[], val: string) => arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

  const buildSearchParams = (search: SavedSearch): URLSearchParams => {
    const params = new URLSearchParams();
    if (search.keywords) params.set('keywords', search.keywords);
    if (search.location && search.location.length > 0) params.set('location', search.location.join(','));
    if (search.field_ids?.length) params.set('fieldIds', search.field_ids.join(','));
    if (search.tipas_ids?.length) params.set('tipasIds', search.tipas_ids.join(','));
    if (search.position_ids?.length) params.set('positionIds', search.position_ids.join(','));
    if (!search.field_ids?.length && !search.tipas_ids?.length && !search.position_ids?.length && search.work_field?.length) {
      params.set('workField', search.work_field.join(','));
    }
    if (search.job_type && search.job_type.length > 0) params.set('jobType', search.job_type.join(','));
    if (search.work_time && search.work_time.length > 0) params.set('workTime', search.work_time.join(','));
    if (search.salary_min) params.set('salaryMin', search.salary_min.toString());
    if (search.company_activity) params.set('companyActivity', search.company_activity);
    return params;
  };

  const fetchSearchCounts = useCallback(async (searches: SavedSearch[]) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const counts: Record<string | number, { total: number; newCount: number }> = {};

    const totalFrom = async (params: URLSearchParams): Promise<number> => {
      params.set('limit', '1');
      const res = await fetch(`${API_URL}/jobs/search/smart?${params.toString()}`);
      if (!res.ok) return 0;
      const data = await res.json();
      return data?.pagination?.total ?? 0;
    };

    await Promise.all(searches.map(async (search) => {
      try {
        const params = buildSearchParams(search);
        const paramsNew = new URLSearchParams(params);
        paramsNew.set('postedWithin', '3');

        const [total, newCount] = await Promise.all([
          totalFrom(new URLSearchParams(params)),
          totalFrom(paramsNew),
        ]);

        counts[search.id] = { total, newCount };
      } catch (error) {
        console.error(`Error fetching count for search ${search.id}:`, error);
        counts[search.id] = { total: 0, newCount: 0 };
      }
    }));

    setSearchCounts(counts);
  }, []);

  useEffect(() => {
    fetchSavedSearches();
  }, [isAuthenticated, isEmployerMode, fetchSavedSearches]);

  useEffect(() => {
    if (savedSearches.length > 0) {
      fetchSearchCounts(savedSearches);
    }
  }, [savedSearches, fetchSearchCounts]);

  const handleCreateSearch = () => openModal(null);

  const handleEditSearch = (search: SavedSearch) => openModal(search);

  const handleSaveSearch = async () => {
    if (!formTitle.trim()) { showToast('Prašome įvesti pavadinimą', 'error'); return; }

    if (!isAuthenticated) {
      const data = { name: formTitle, keywords: formKeywords, location: formLocation, work_field: [], field_ids: formWorkFields.fieldIds, tipas_ids: formWorkFields.tipasIds, position_ids: formWorkFields.positionIds, job_type: formJobType, work_time: formWorkTime, salary_min: formSalaryMin ? parseFloat(formSalaryMin) : null };
      if (editingSearch) updateLocalSavedSearch(String(editingSearch.id), data);
      else addLocalSavedSearch(data);
      showToast(editingSearch ? 'Paieška atnaujinta!' : 'Paieška išsaugota!', 'success');
      setShowCreateModal(false);
      fetchSavedSearches();
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const payload = {
        title: formTitle,
        keywords: formKeywords.length > 0 ? formKeywords.join(', ') : null,
        location: formLocation.length > 0 ? formLocation : null,
        work_field: null,
        field_ids: formWorkFields.fieldIds.length > 0 ? formWorkFields.fieldIds : null,
        tipas_ids: formWorkFields.tipasIds.length > 0 ? formWorkFields.tipasIds : null,
        position_ids: formWorkFields.positionIds.length > 0 ? formWorkFields.positionIds : null,
        job_type: formJobType.length > 0 ? formJobType : null,
        work_time: formWorkTime.length > 0 ? formWorkTime : null,
        salary_min: formSalaryMin ? parseFloat(formSalaryMin) : null,
        company_activity: formCompanyActivity || null,
        is_subscribed: formIsSubscribed,
        notification_frequency: formFrequency,
      };
      const baseEndpoint = isEmployerMode ? 'employer-saved-searches' : 'saved-searches';
      const url = editingSearch ? `${API_URL}/${baseEndpoint}/${editingSearch.id}` : `${API_URL}/${baseEndpoint}`;
      const response = await fetch(url, { method: editingSearch ? 'PUT' : 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (response.ok) {
        showToast(editingSearch ? 'Paieška atnaujinta!' : 'Paieška išsaugota!', 'success');
        setShowCreateModal(false);
        fetchSavedSearches();
      } else { showToast('Nepavyko išsaugoti paieškos', 'error'); }
    } catch (error) {
      showToast('Įvyko klaida išsaugant paiešką', 'error');
    }
  };

  const handleDeleteSearch = (id: number | string) => {
    setSearchToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!searchToDelete) return;

    if (!isAuthenticated) {
      deleteLocalSavedSearch(String(searchToDelete));
      fetchSavedSearches();
      setShowDeleteConfirm(false);
      setSearchToDelete(null);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const baseEndpoint = isEmployerMode ? 'employer-saved-searches' : 'saved-searches';
      const response = await fetch(`${API_URL}/${baseEndpoint}/${searchToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchSavedSearches();
      } else {
        showToast('Nepavyko ištrinti paieškos', 'error');
      }
    } catch (error) {
      console.error('Error deleting search:', error);
      showToast('Įvyko klaida trinant paiešką', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setSearchToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSearchToDelete(null);
  };

  const handleToggleSelectAll = () => {
    const filteredSearches = filterSearchesByTitle(savedSearches);
    
    if (selectedSearches.length === filteredSearches.length && filteredSearches.length > 0) {
      setSelectedSearches([]);
    } else {
      setSelectedSearches(filteredSearches.map(s => s.id));
    }
  };

  const handleToggleSelect = (id: number | string) => {
    setSelectedSearches(prev => 
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const openModal = (search: SavedSearch | null) => {
    setEditingSearch(search);
    setFormTitle(search ? search.title : '');
    setFormKeywords(search?.keywords ? search.keywords.split(',').map(k => k.trim()).filter(Boolean) : []);
    setFormLocation(search?.location || []);
    setFormWorkFields({
      fieldIds: search?.field_ids || [],
      tipasIds: search?.tipas_ids || [],
      positionIds: search?.position_ids || [],
    });
    setFormWorkFieldLabels([]);
    setFormJobType(search?.job_type || []);
    setFormWorkTime(search?.work_time || []);
    setFormSalaryMin(search?.salary_min?.toString() || '');
    setFormSalarySlider(search?.salary_min || 0);
    setFormCompanyActivity(search?.company_activity || '');
    setFormCompanyNames([]);
    setFormIsSubscribed(search ? search.is_subscribed : true);
    setFormFrequency((search?.notification_frequency as any) || 'daily');
    setActiveFilterPanel(null);
    setLocationSearch('');
    setCompanySearch('');
    setKeywordInputVal('');
    setShowCreateModal(true);
  };

  const handleDeleteSelected = async () => {
    if (selectedSearches.length === 0) return;

    const confirmMessage = `Ar tikrai norite ištrinti ${selectedSearches.length} paieškų? Šio veiksmo negalima atšaukti.`;
    if (!await confirm(confirmMessage)) return;

    if (!isAuthenticated) {
      selectedSearches.forEach(id => deleteLocalSavedSearch(String(id)));
      setSelectedSearches([]);
      fetchSavedSearches();
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const baseEndpoint = isEmployerMode ? 'employer-saved-searches' : 'saved-searches';

      await Promise.all(
        selectedSearches.map(id =>
          fetch(`${API_URL}/${baseEndpoint}/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
        )
      );

      setSelectedSearches([]);
      fetchSavedSearches();
    } catch (error) {
      console.error('Error deleting searches:', error);
      showToast('Įvyko klaida trinant paieškas', 'error');
    }
  };

  const filterSearchesByTitle = (searches: SavedSearch[]) => {
    if (!searchTitleFilter.trim()) return searches;
    
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return searches.filter(search => 
      search.title.toLowerCase().includes(filterLower)
    );
  };

  const handleToggleSubscription = async (id: number | string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const baseEndpoint = isEmployerMode ? 'employer-saved-searches' : 'saved-searches';
      const response = await fetch(`${API_URL}/${baseEndpoint}/${id}/toggle-subscription`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const current = savedSearches.find(s => s.id === id);
        const wasSubscribed = current?.is_subscribed;
        showToast(
          wasSubscribed ? 'Prenumerata išjungta' : 'Prenumerata įjungta! Paieška rodoma Prenumeratose.',
          wasSubscribed ? 'info' : 'success'
        );
        fetchSavedSearches();
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
    }
  };

  const handleGoToSearch = (search: SavedSearch) => {
    console.log('🔍 handleGoToSearch - Full search object:', search);
    console.log('🔍 handleGoToSearch - positions field:', search.positions);
    
    // Build search URL with filters
    const params = new URLSearchParams();
    
    if (isEmployerMode) {
      // Employer candidate search
      // Note: No need to set mode param anymore, route handles it
      
      // Positions (keywords) - parse if it's a JSON string
      if (search.positions) {
        console.log('🔍 handleGoToSearch - positions exists:', search.positions);
        try {
          const positionsArray = Array.isArray(search.positions) 
            ? search.positions 
            : JSON.parse(search.positions as string);
          console.log('🔍 handleGoToSearch - positionsArray:', positionsArray);
          if (positionsArray.length > 0) {
            params.set('keywords', positionsArray.join(','));
            console.log('✅ handleGoToSearch - Set keywords param:', positionsArray.join(','));
          }
        } catch (e) {
          console.error('❌ Error parsing positions:', e);
        }
      } else {
        console.log('❌ handleGoToSearch - No positions field!');
      }
      
      if (search.location && search.location.length > 0) {
        params.set('location', search.location.join(','));
      }
      
      console.log('🔍 handleGoToSearch - Final URL:', `/employer/candidates?${params.toString()}`);
      navigate(`/employer/candidates?${params.toString()}`);
    } else {
      // Employee job search
      if (search.keywords) params.set('keywords', search.keywords);
      if (search.location && search.location.length > 0) params.set('location', search.location.join(','));
      if (search.field_ids?.length) params.set('fieldIds', search.field_ids.join(','));
      if (search.tipas_ids?.length) params.set('tipasIds', search.tipas_ids.join(','));
      if (search.position_ids?.length) params.set('positionIds', search.position_ids.join(','));
      if (!search.field_ids?.length && !search.tipas_ids?.length && !search.position_ids?.length && search.work_field?.length) {
        params.set('workField', search.work_field.join(','));
      }
      if (search.job_type && search.job_type.length > 0) params.set('jobType', search.job_type.join(','));
      if (search.work_time && search.work_time.length > 0) params.set('workTime', search.work_time.join(','));
      if (search.salary_min) params.set('salaryMin', search.salary_min.toString());
      if (search.company_activity) params.set('companyActivity', search.company_activity);

      navigate(`/darbo-skelbimai?${params.toString()}`);
    }
  };

  const formatCriteriaFlat = (search: SavedSearch): string => {
    const parts: string[] = [];

    if (search.keywords) parts.push(search.keywords);

    if (search.positions) {
      try {
        const arr = Array.isArray(search.positions) ? search.positions : JSON.parse(search.positions as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.location && search.location.length > 0) parts.push(...search.location);

    if (search.education_level) {
      try {
        const arr = Array.isArray(search.education_level) ? search.education_level : JSON.parse(search.education_level as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.languages) {
      try {
        const arr = Array.isArray(search.languages) ? search.languages : JSON.parse(search.languages as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.technical_skills) {
      try {
        const arr = Array.isArray(search.technical_skills) ? search.technical_skills : JSON.parse(search.technical_skills as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.experience_years) parts.push(search.experience_years);

    if (search.desired_work_type) {
      try {
        const arr = Array.isArray(search.desired_work_type) ? search.desired_work_type : JSON.parse(search.desired_work_type as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.desired_salary) parts.push(search.desired_salary);
    const fieldLabels = (search.field_ids || []).map((id) => taxFieldNames[id]).filter(Boolean) as string[];
    const tipasLabels = (search.tipas_ids || []).map((id) => taxTipasNames[id]).filter(Boolean) as string[];
    if (fieldLabels.length) parts.push(...fieldLabels);
    if (tipasLabels.length) parts.push(...tipasLabels);
    if (search.position_ids?.length) parts.push(`${search.position_ids.length} pareigybės`);
    if (!fieldLabels.length && !tipasLabels.length && !search.position_ids?.length && search.work_field && search.work_field.length > 0) parts.push(...search.work_field);
    if (search.job_type && search.job_type.length > 0) parts.push(...search.job_type);
    if (search.salary_min) parts.push(`nuo ${search.salary_min}€`);

    return parts.length > 0 ? parts.join(', ') : 'Visi skelbimai';
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="saved-searches-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if wrong user type for the mode (only for authenticated users)
  if (isAuthenticated && isEmployerMode && user?.user_type !== 'employer') {
    return null;
  }
  if (isAuthenticated && !isEmployerMode && user?.user_type !== 'employee' && user?.user_type !== 'job_seeker') {
    return null;
  }

  return (
    <div className="saved-searches-page">
      {ConfirmDialogNode}
      <div className="container">
        {/* Header row: breadcrumbs left, filters right */}
        <div className="header-row">
          <div className="breadcrumbs">
            <span onClick={() => navigate('/')}>Pradžia</span>
            <span className="separator">&gt;</span>
            <span className="current">Išsaugotos paieškos</span>
          </div>
          <div className="filters-toolbar">
            <SelectAllToggle
              selectedCount={selectedSearches.length}
              totalCount={filterSearchesByTitle(savedSearches).length}
              onToggle={handleToggleSelectAll}
            />
            <button 
              className="btn-delete"
              onClick={handleDeleteSelected}
              disabled={selectedSearches.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Paieškos pavadinimas"
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <Search size={18} className="search-icon" />
            </div>
            <button className="btn-new-search" onClick={handleCreateSearch}>
              <Plus size={18} />
              <span>Nauja paieška</span>
            </button>
          </div>
        </div>

        {/* Searches Content */}
        <div className="searches-section">
          {loading ? (
            <div className="loading">Kraunama...</div>
          ) : filterSearchesByTitle(savedSearches).length === 0 ? (
            <div className="empty-state">
              <Search size={48} />
              <p>{searchTitleFilter ? 'Paieškų pagal šį filtrą nerasta' : 'Dar neturite išsaugotų paieškų'}</p>
              {!searchTitleFilter && (
                <button className="btn-primary" onClick={handleCreateSearch}>
                  Sukurti pirmą paiešką
                </button>
              )}
            </div>
          ) : (
            <div className="searches-grid">
              {filterSearchesByTitle(savedSearches).map((search) => (
                <div key={search.id} className={`search-card ${selectedSearches.includes(search.id) ? 'selected' : ''}`}>
                  <div className="search-card-header">
                    <div className="header-left">
                      <input
                        type="checkbox"
                        checked={selectedSearches.includes(search.id)}
                        onChange={() => handleToggleSelect(search.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {isAuthenticated && (
                        <button
                          className={`subscription-label ${search.is_subscribed ? 'active' : ''}`}
                          onClick={() => handleToggleSubscription(search.id)}
                        >
                          {search.is_subscribed ? <Bell size={14} /> : <BellOff size={14} />}
                          <span>{search.is_subscribed ? 'Prenumeruojama' : 'Prenumeruoti'}</span>
                        </button>
                      )}
                    </div>
                    <div className="header-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleEditSearch(search)}
                        title="Nustatymai"
                      >
                        <Settings size={16} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleDeleteSearch(search.id)}
                        title="Ištrinti"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="search-card-body" onClick={() => handleGoToSearch(search)}>
                    <h3 className="search-title">{search.title}</h3>
                    <div className="search-criteria">
                      <span className="criteria-label">Kriterijai:</span>
                      <p className="criteria-text">{formatCriteriaFlat(search)}</p>
                    </div>
                  </div>

                  <div className="search-card-footer">
                    <span className="result-count">
                      Iš viso: <strong>{searchCounts[search.id]?.total ?? '...'}</strong>
                    </span>
                    <span className="divider" />
                    <span className="new-count">
                      Naujų: <strong>{searchCounts[search.id]?.newCount ?? '...'}</strong>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal — card-based UI */}
      {showCreateModal && (
        <div className="modal-overlay prenumeratos-page" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content pren-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSearch ? 'Redaguoti paiešką' : 'Nauja paieška'}</h2>
              <button className="btn-close" onClick={() => setShowCreateModal(false)}><X size={20}/></button>
            </div>

            <div className="pren-modal-body">
              {/* Name */}
              <div className="pren-name-row">
                <div className="form-group">
                  <label>Paieškos pavadinimas *</label>
                  <input type="text" value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="pvz., Programuotojas Vilniuje" />
                </div>
              </div>

              {/* Filter cards */}
              <div className="pren-filter-cards">
                {[
                  { key: 'location',   icon: <MapPin size={24}/>,    label: 'Darbo vietos',  chips: formLocation },
                  { key: 'workField',  icon: <Briefcase size={24}/>, label: 'Darbo sritys',  chips: formWorkFieldLabels },
                  { key: 'companies',  icon: <Building size={24}/>,  label: 'Įmonės',        chips: [...(formCompanyActivity ? [formCompanyActivity] : []), ...formCompanyNames] },
                  { key: 'jobType',    icon: <Clock size={24}/>,     label: 'Darbo tipas',   chips: formJobType },
                  { key: 'workTime',   icon: <Calendar size={24}/>,  label: 'Darbo laikas',  chips: formWorkTime },
                  { key: 'salary',     icon: <Euro size={24}/>,      label: 'Atlyginimas',   chips: formSalaryMin ? [`Nuo ${formSalaryMin} €`] : [] },
                ].map(({ key, icon, label, chips }) => (
                  <div key={key} className="pren-filter-card" onClick={() => setActiveFilterPanel(key)}>
                    <div className="pren-card-header">
                      <span className="pren-card-icon-wrap">
                        {icon}
                        {chips.length > 0 && <span className="pren-filter-badge">{chips.length}</span>}
                      </span>
                      <span className="pren-card-label">{label}</span>
                    </div>
                    {chips.length > 0 ? (
                      <div className="pren-card-chips">
                        {chips.map(v => <span key={v} className="pren-selected-chip">{v}</span>)}
                      </div>
                    ) : (
                      <button type="button" className="pren-card-select-btn"><span>+</span> PASIRINKTI</button>
                    )}
                  </div>
                ))}
              </div>

            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>Atšaukti</button>
              <button className="btn-primary" onClick={handleSaveSearch} disabled={!formTitle.trim()}>{editingSearch ? 'Atnaujinti' : 'Išsaugoti'}</button>
            </div>
          </div>
        </div>
      )}

      {/* RAKTAŽODŽIAI */}
      <FilterModal isOpen={activeFilterPanel === 'keywords'} onClose={() => setActiveFilterPanel(null)} title="Raktažodžiai" icon={<Search size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input type="text" className="filter-input" placeholder="pvz., programuotojas" value={keywordInputVal} onChange={e => setKeywordInputVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); const v = keywordInputVal.trim(); if (v && !formKeywords.includes(v)) setFormKeywords(p => [...p, v]); setKeywordInputVal(''); }}} />
          <button onClick={() => { const v = keywordInputVal.trim(); if (v && !formKeywords.includes(v)) setFormKeywords(p => [...p, v]); setKeywordInputVal(''); }} style={{ padding: '0 12px', background: '#13B69B', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>+</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {formKeywords.map(kw => (
            <span key={kw} style={{ background: '#e8f7f5', color: '#13B69B', border: '1px solid #b2e4dc', borderRadius: 12, padding: '3px 10px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
              {kw} <button onClick={() => setFormKeywords(p => p.filter(v => v !== kw))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#13B69B', fontSize: 16, lineHeight: 1 }}>×</button>
            </span>
          ))}
        </div>
      </FilterModal>

      {/* DARBO VIETOS */}
      <FilterModal isOpen={activeFilterPanel === 'location'} onClose={() => setActiveFilterPanel(null)} title="Darbo vietos" icon={<MapPin size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <input type="text" className="filter-input" placeholder="Ieškoti miesto..." value={locationSearch} onChange={e => setLocationSearch(e.target.value)} />
        <div className="cities-grid">
          {LITHUANIAN_CITIES.filter(c => !locationSearch || c.toLowerCase().includes(locationSearch.toLowerCase())).map(city => (
            <label key={city} className="city-checkbox-label"><input type="checkbox" checked={formLocation.includes(city)} onChange={() => setFormLocation(p => toggleArr(p, city))} /><span>{city}</span></label>
          ))}
        </div>
      </FilterModal>

      {/* DARBO SRITYS */}
      <FilterModal isOpen={activeFilterPanel === 'workField'} onClose={() => setActiveFilterPanel(null)} title="Darbo sritys" icon={<Briefcase size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <WorkFieldCascadePicker
          value={formWorkFields}
          onChange={setFormWorkFields}
          onLabelsChange={setFormWorkFieldLabels}
        />
      </FilterModal>

      {/* ĮMONĖS */}
      <FilterModal isOpen={activeFilterPanel === 'companies'} onClose={() => setActiveFilterPanel(null)} title="Įmonės" icon={<Building size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="two-column-layout">
          <div className="filter-column">
            <h4>Veiklos sritys</h4>
            <div className="checkbox-list" style={{ maxHeight: 360, overflowY: 'auto' }}>
              {Array.from(new Map(companies.filter(c => c.company_activity?.trim()).map(c => [c.company_activity!, c])).values()).map(c => (
                <label key={c.company_activity} className="checkbox-label">
                  <input type="radio" name="ss-company-activity" checked={formCompanyActivity === c.company_activity} onChange={() => setFormCompanyActivity(formCompanyActivity === c.company_activity ? '' : c.company_activity!)} />
                  <span>{c.company_activity}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="filter-column">
            <h4>Įmonės</h4>
            <input type="text" className="filter-input" placeholder="Įvesti įmonės pavadinimą..." value={companySearch} onChange={e => setCompanySearch(e.target.value)} />
            <div className="checkbox-list" style={{ maxHeight: 320, overflowY: 'auto' }}>
              {companies.filter(c => (!formCompanyActivity || c.company_activity === formCompanyActivity) && (!companySearch || c.company_name.toLowerCase().includes(companySearch.toLowerCase()))).map(c => (
                <label key={c.company_name} className="checkbox-label">
                  <input type="checkbox" checked={formCompanyNames.includes(c.company_name)} onChange={() => setFormCompanyNames(p => toggleArr(p, c.company_name))} />
                  <span>{c.company_name} ({c.job_count})</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </FilterModal>

      {/* DARBO TIPAS */}
      <FilterModal isOpen={activeFilterPanel === 'jobType'} onClose={() => setActiveFilterPanel(null)} title="Darbo tipas" icon={<Clock size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="checkbox-list single-column">
          {(cvTemplates.work_type || ['Nuolatinis','Laikinas','Projektinis','Praktika'].map((v,i)=>({id:i,value:v}))).map((t: any) => (
            <label key={t.id} className="checkbox-label"><input type="checkbox" checked={formJobType.includes(t.value)} onChange={() => setFormJobType(p => toggleArr(p, t.value))} /><span>{t.value}</span></label>
          ))}
        </div>
      </FilterModal>

      {/* DARBO LAIKAS */}
      <FilterModal isOpen={activeFilterPanel === 'workTime'} onClose={() => setActiveFilterPanel(null)} title="Darbo laikas" icon={<Calendar size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="checkbox-list single-column">
          {(cvTemplates.work_time || ['Visą darbo dieną','Nepilną darbo dieną','Nuotoliniu būdu','Hibridinis'].map((v,i)=>({id:i,value:v}))).map((t: any) => (
            <label key={t.id} className="checkbox-label"><input type="checkbox" checked={formWorkTime.includes(t.value)} onChange={() => setFormWorkTime(p => toggleArr(p, t.value))} /><span>{t.value}</span></label>
          ))}
        </div>
      </FilterModal>

      {/* ATLYGINIMAS */}
      <FilterModal isOpen={activeFilterPanel === 'salary'} onClose={() => setActiveFilterPanel(null)} title="Atlyginimas" icon={<Euro size={20}/>} onShowResults={() => setActiveFilterPanel(null)}>
        <div className="salary-slider-wrap">
          <div className="salary-slider-label">{formSalarySlider === 0 ? 'Bet koks atlyginimas' : `Nuo ${formSalarySlider.toLocaleString('lt-LT')} €`}</div>
          <input type="range" min={0} max={10000} step={50} value={formSalarySlider}
            style={{ '--slider-pct': `${(formSalarySlider/10000)*100}%` } as React.CSSProperties}
            onChange={e => { const v = parseInt(e.target.value,10); setFormSalarySlider(v); setFormSalaryMin(v === 0 ? '' : String(v)); }} />
          <div className="salary-slider-range"><span>0 €</span><span>10 000 €</span></div>
        </div>
      </FilterModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        message="Ar tikrai norite visiškai ištrinti šią paiešką? Šio veiksmo negalima atšaukti."
        confirmText="OK"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default SavedSearches;

